/* global axios */
import { ref, computed, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const FUP_BASE_URL = '/api/v1/nurture_fup';

export function useFollowUpStatus() {
  const currentChat = useMapGetter('getSelectedChat');
  const currentUser = useMapGetter('getCurrentUser');

  const fupData = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
  const actionLoading = ref(false);

  const hasFupSessions = computed(() => fupData.value?.fup_sessoes?.length > 0);

  const activeSessions = computed(() => fupData.value?.fup_sessoes || []);

  const availableTypes = computed(
    () => fupData.value?.fup_tipos_disponiveis || []
  );

  const clientId = computed(() => fupData.value?.client_id || null);
  const clientNome = computed(() => fupData.value?.client_nome || null);

  function getPhoneNumber() {
    const sender = currentChat.value?.meta?.sender;
    return sender?.phone_number || '';
  }

  function getAccountId() {
    return currentChat.value?.account_id || currentUser.value?.account_id;
  }

  async function fetchFupStatus() {
    const phone = getPhoneNumber();
    if (!phone) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[FUP] Contato sem número de telefone, banner não será exibido'
        );
      }
      fupData.value = null;
      return;
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[FUP] Buscando status para telefone:', phone);
    }
    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await axios.post(`${FUP_BASE_URL}/status`, {
        phone_number: phone,
        account_id: getAccountId(),
      });
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[FUP] Dados recebidos:', data);
      }
      fupData.value = data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[FUP] Erro ao buscar status:', err);
      error.value = err.response?.data?.error || err.message;
      fupData.value = null;
    } finally {
      isLoading.value = false;
    }
  }

  const companyInstanceWppId = computed(
    () => fupData.value?.company_instance_wpp_id || null
  );

  async function createFup(fupTipoId, variaveis = {}) {
    const phone = getPhoneNumber();
    const sender = currentChat.value?.meta?.sender;

    actionLoading.value = true;
    error.value = null;

    try {
      const { data } = await axios.post(`${FUP_BASE_URL}/action`, {
        action: 'criar',
        phone_number: phone,
        contact_name: sender?.name || '',
        fup_tipo_id: fupTipoId,
        company_instance_wpp_id: companyInstanceWppId.value,
        account_id: getAccountId(),
        variaveis,
      });

      if (data.success) {
        await fetchFupStatus();
      }
      return data;
    } catch (err) {
      error.value = err.response?.data?.error || err.message;
      return { success: false, error: error.value };
    } finally {
      actionLoading.value = false;
    }
  }

  async function performAction(action, sessaoId) {
    actionLoading.value = true;
    error.value = null;

    try {
      const { data } = await axios.post(`${FUP_BASE_URL}/action`, {
        action,
        sessao_id: sessaoId,
        phone_number: getPhoneNumber(),
        company_instance_wpp_id: companyInstanceWppId.value,
        account_id: getAccountId(),
      });

      if (data.success) {
        await fetchFupStatus();
      }
      return data;
    } catch (err) {
      error.value = err.response?.data?.error || err.message;
      return { success: false, error: error.value };
    } finally {
      actionLoading.value = false;
    }
  }

  async function pauseFup(sessaoId) {
    return performAction('pausar', sessaoId);
  }

  async function resumeFup(sessaoId) {
    return performAction('retomar', sessaoId);
  }

  async function cancelFup(sessaoId) {
    return performAction('cancelar', sessaoId);
  }

  // Re-fetch when conversation changes
  watch(
    () => currentChat.value?.id,
    newId => {
      if (newId) {
        fetchFupStatus();
      } else {
        fupData.value = null;
      }
    },
    { immediate: true }
  );

  return {
    fupData,
    isLoading,
    error,
    actionLoading,
    hasFupSessions,
    activeSessions,
    availableTypes,
    clientId,
    clientNome,
    fetchFupStatus,
    createFup,
    pauseFup,
    resumeFup,
    cancelFup,
  };
}
